/**

 * GoPay MoR §14 — named methods + payment marks on tenant shop/checkout.

 * Assets: /providers/{visa,mastercard,visa-secure,mastercard-id-check,gopay}.svg

 * GoPay mark must link-back www.gopay.cz.

 * Matrix §14 Fail while any mark is unlicensed / placeholder

 * (see shared/public/providers/ATTRIBUTION.md).

 */

import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { isUnlicensedPaymentMarkSvg } from './isUnlicensedPaymentMarkSvg.js';



const MARKS = [

  { id: 'visa', file: 'visa.svg', namedKey: 'morChrome.methods.visa' as const },

  {

    id: 'mastercard',

    file: 'mastercard.svg',

    namedKey: 'morChrome.methods.mastercard' as const,

  },

  {

    id: 'visa-secure',

    file: 'visa-secure.svg',

    namedKey: 'morChrome.methods.visaSecure' as const,

  },

  {

    id: 'mastercard-id-check',

    file: 'mastercard-id-check.svg',

    namedKey: 'morChrome.methods.mastercardIdCheck' as const,

  },

  {

    id: 'gopay',

    file: 'gopay.svg',

    namedKey: 'morChrome.methods.gopay' as const,

    gopayLink: true as const,

  },

] as const;



export interface PaymentMarksStripProps {

  readonly testIdPrefix?: string;

  readonly compact?: boolean;

}



export function PaymentMarksStrip({

  testIdPrefix = 'customer-payment-marks',

  compact = false,

}: PaymentMarksStripProps): JSX.Element {

  const { t } = useTranslation('customer');

  const [unlicensed, setUnlicensed] = useState(false);



  useEffect(() => {

    let cancelled = false;

    const controllers = MARKS.map(() => new AbortController());



    void (async () => {

      const results = await Promise.all(

        MARKS.map(async (mark, index) => {

          const src = `/providers/${mark.file}`;

          try {

            const res = await fetch(src, {

              signal: controllers[index]?.signal,

              cache: 'no-cache',

            });

            if (!res.ok) {

              return true;

            }

            const text = await res.text();

            return isUnlicensedPaymentMarkSvg(text);

          } catch {

            return true;

          }

        }),

      );

      if (!cancelled) {

        setUnlicensed(results.some(Boolean));

      }

    })();



    return () => {

      cancelled = true;

      for (const c of controllers) {

        c.abort();

      }

    };

  }, []);



  return (

    <section

      className={

        compact

          ? 'flex min-w-0 flex-col gap-2'

          : 'flex min-w-0 flex-col gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3'

      }

      data-testid={testIdPrefix}

      aria-label={t('morChrome.paymentMarks.ariaLabel')}

    >

      <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-[var(--color-on-surface-muted)]">

        {t('morChrome.paymentMarks.heading')}

      </h3>

      <p

        className="m-0 text-sm text-[var(--color-on-surface)]"

        data-testid={`${testIdPrefix}-named`}

      >

        {t('morChrome.paymentMarks.namedMethods')}

      </p>

      {unlicensed ? (

        <p

          className="m-0 rounded border border-[var(--color-danger,#b42318)] bg-[var(--color-danger-subtle,#fef3f2)] px-2 py-1.5 text-sm text-[var(--color-danger,#b42318)]"

          data-testid="payment-marks-unlicensed"

          role="status"

        >

          {t('morChrome.paymentMarks.unlicensedWarning', {

            defaultValue:

              'Payment marks are unlicensed placeholders. GoPay matrix §14 = Fail until the Brand Center kit lands.',

          })}

        </p>

      ) : null}

      <ul

        className="m-0 flex list-none flex-wrap items-center gap-3 p-0"

        data-testid={`${testIdPrefix}-logos`}

      >

        {MARKS.map((mark) => {

          const img = (

            <img

              src={`/providers/${mark.file}`}

              alt={t(mark.namedKey)}

              className="h-8 w-auto max-w-[4.5rem] object-contain"

              loading="lazy"

              decoding="async"

              data-testid={`${testIdPrefix}-${mark.id}`}

            />

          );

          return (

            <li key={mark.id} className="flex items-center">

              {'gopayLink' in mark && mark.gopayLink === true ? (

                <a

                  href="https://www.gopay.cz"

                  target="_blank"

                  rel="noopener noreferrer"

                  className="inline-flex"

                  data-testid={`${testIdPrefix}-gopay-link`}

                  aria-label={t('morChrome.paymentMarks.gopayLinkAria')}

                >

                  {img}

                </a>

              ) : (

                img

              )}

            </li>

          );

        })}

      </ul>

    </section>

  );

}

